/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SessionService", ["InitService", "EventListener"], function (context, cls) {
  /**
   * Service that manages Sessions
   * @namespace gbc.SessionService
   * @gbcService
   * @publicdoc
   */
  class SessionService /** @lends gbc.SessionService */ {
    constructor() {
      // EVENT labels
      /** @private */
      this._sessionAdded = "sessionAdded";
      /** @private */
      this._sessionRemoved = "sessionRemoved";

      /**
       * session identifier
       * @type {Number}
       * @private
       */
      this._identifier = 0;
      /** @type {classes.VMSession[]} */
      this._sessions = [];
      /** @type {Object} */
      this._bySessionId = {};
      /** @type {classes.EventListener} */
      this._eventListener = new cls.EventListener();
    }

    destroy() {
      this._bySessionId = null;
      this._sessions.length = 0;
      this._sessions = null;
      this._eventListener.destroy();
      this._eventListener = null;
    }

    /**
     * Initialize the session Service
     */
    init() {
      return;
    }

    /**
     * Start an application
     * @param appName {string}
     * @param params {Object}
     * @param procId {string}
     * @returns {classes.VMSession}
     */
    startApplication(appName, params, procId) {
      const session = new cls.VMSession(this._identifier++);
      this._eventListener.emit(this._sessionAdded, session);
      const subAppInfo = context.bootstrapInfo.subAppInfo;
      if (subAppInfo) {
        let parentSession = context.BrowserWindowsService.getParentSession();
        session._baseInfos = parentSession && parentSession._baseInfos;
        session.startSubTask(subAppInfo, (app) => {
          if (procId) {
            app.info().connectionInfo = app.info().connectionInfo || {};
            app.info().procId = procId;
            app.info().connectionInfo.procId = procId;
          }
        });
      } else {
        let app = session.start(appName, params);
        if (procId) {
          app.info().connectionInfo = app.info().connectionInfo || {};
          app.info().procId = procId;
          app.info().connectionInfo.procId = procId;
        }
      }

      this._sessions.push(session);
      context.HostService.displaySession();
      return session;
    }

    /**
     * Start via a connection
     * @param {window.gbcWrapper} wrapper
     * @param {Object<string, *>} readyData
     * @return {*}
     */
    startDirect(wrapper, readyData) {
      let session = null;
      if (this._sessions.length) {
        session = this._sessions[0];
      } else {
        session = new cls.VMSession(this._identifier++);
        this._sessions.push(session);
        this._eventListener.emit(this._sessionAdded, session);
        context.HostService.displaySession();
      }
      session.startDirect(wrapper, readyData);
      return session;
    }

    fromSessionId(id) {
      return this._bySessionId[id];
    }

    updateSessionId(session, id) {
      this._bySessionId[id] = session;
    }

    /**
     * Remove a session
     * @param session
     * @param restarting
     */
    remove(session, restarting) {
      this._bySessionId[session.getSessionId()] = null;
      cls.ArrayHelper.remove(this._sessions, session);
      this._eventListener.emit(this._sessionRemoved, session);
      if (this._sessions.length || restarting) {
        return;
      }
      let baseURI = document.baseURI;
      if (!baseURI) {
        const base = document.getElementsByTagName("base");
        if (base.length) {
          baseURI = base[0].href || "";
        }
      }
      context.UrlService.setCurrentUrl(baseURI.replace(/\/$/, "/index.html"));
      context.HostService.displayNoSession();
    }

    /**
     * Return active sessions
     * @returns {classes.VMSession[]}
     */
    getSessions() {
      return this._sessions;
    }

    /**
     * Return a session by id
     * @param identifier {string} id of the session to get
     * @return {classes.VMSession} the requested session
     */
    getSession(identifier) {
      return this._sessions.filter(function (item) {
        return item.getIdentifier() === identifier;
      })[0];
    }

    /**
     * Get the current running session
     * @returns {classes.VMSession} the current session
     * @publicdoc
     */
    getCurrent() {
      if (this._sessions) {
        return this._sessions.at(-1);
      }
      return null;
    }

    /**
     * Handler called once a session has been added
     * BE CAREFULL : At this point the Session is not registered as Current Session
     * @param hook
     * @return {*|HandleRegistration|promise.Promise<any>|Q.Promise<void>|Q.Promise<string>}
     */
    onSessionAdded(hook) {
      return this._eventListener.when(this._sessionAdded, hook);
    }
    /**
     * Handler called once a sesion has been removed
     * @param hook
     * @return {*|HandleRegistration|promise.Promise<any>|Q.Promise<void>|Q.Promise<string>}
     */
    onSessionRemoved(hook) {
      return this._eventListener.when(this._sessionRemoved, hook);
    }
  }

  gbc.SessionService = new SessionService();

  context.InitService.register(context.SessionService);
});
